import { BaseStore } from "@infrastructure/stores/_base.store";
import { autorun, when } from "mobx";
import { AuthStore } from "@infrastructure/stores/auth.store";
import { MachineClient } from "@infrastructure/clients/http/clients/machine.client";
import { ServiceType, MachineStatus } from "@domain/enums/machine.enums";

export interface MachineListItem {
  id: number;
  latitude: number | null;
  longitude: number | null;
  status: MachineStatus;
}

export interface MachineServiceItem {
  id: number;
  name: string;
  service_type: ServiceType;
  service_mode: string;
  available: boolean;
  description?: string | null;
  price_label?: string | null;
  hint?: string | null;
  icon_url?: string | null;
  display_order?: number;
  enabled?: boolean;
}

export interface MachinePhoto {
  id: number;
  url: string;
}

export interface MachineTariff {
  id: number;
  description: string;
}

export interface MachineItem {
  id: number;
  status: MachineStatus;
  title: string;
  description: string;
  address: string;
  working_hours: string;
  free_cells: number;
  machine_number: string;
  services: MachineServiceItem[];
  photos: MachinePhoto[];
  tariffs: MachineTariff[];
  user_can_order?: boolean | null;
  ban_message?: string | null;
}

interface CachedMachineItem {
  data: MachineItem;
  cachedAt: number;
}

const MACHINE_CACHE_TTL_MS = 5 * 60 * 1000;

type MachineStoreState = {
  filteredTypes: ServiceType[];
  excludedMachineIds: number[];
  items: MachineListItem[];
  selectedId: number | null;
  machineCache: Map<string, MachineItem>;
};

class MachineStoreFactory extends BaseStore<MachineStoreState> {
  filteredTypes: ServiceType[] = [];
  excludedMachineIds: number[] = [];
  private items: MachineListItem[] = [];
  private initialized = false;
  private _machineCache = new Map<string, CachedMachineItem>();
  selectedId: number | null = null;

  get allItems() {
    if (this.filteredTypes.length === 0) return this.items;
    return this.items.filter((v) => !this.excludedMachineIds.includes(v.id));
  }

  get machineCache(): Map<string, MachineItem> {
    const now = Date.now();
    const result = new Map<string, MachineItem>();
    for (const [key, entry] of this._machineCache) {
      if (now - entry.cachedAt < MACHINE_CACHE_TTL_MS) {
        result.set(key, entry.data);
      }
    }
    return result;
  }

  constructor() {
    super();
  }

  protected createState(): MachineStoreState {
    return {
      filteredTypes: this.filteredTypes,
      excludedMachineIds: this.excludedMachineIds,
      items: this.allItems,
      selectedId: this.selectedId,
      machineCache: this.machineCache,
    };
  }

  protected getStateKey(): string {
    return `machine:${this.items.length}:filtered:${this.excludedMachineIds.length}:selected:${this.selectedId}`;
  }

  /**
   * Устанавливает типы фильтрации
   */
  setFilteredTypes(types: ServiceType[]) {
    this.filteredTypes = types;
    this.notifyListeners();
  }

  /**
   * Устанавливает исключенные идентификаторы машин
   */
  setExcludedMachineIds(ids: number[]) {
    this.excludedMachineIds = ids;
    this.notifyListeners();
  }

  /**
   * Устанавливает список машин
   */
  setItems(items: MachineListItem[]) {
    this.items = items;
    this.notifyListeners();
  }

  /**
   * Выбирает машину
   */
  selectMachine(id: number | null) {
    this.selectedId = id;
    this.notifyListeners();
  }

  /**
   * Инициализирует store
   */
  async init() {
    if (this.initialized) return;

    if (!AuthStore || !AuthStore.auth) {
      await new Promise<void>((resolve) => {
        const checkAuthStore = () => {
          if (AuthStore && AuthStore.auth) {
            resolve();
          } else {
            setTimeout(checkAuthStore, 10);
          }
        };
        checkAuthStore();
      });
    }

    // Ждем пока пользователь не будет авторизован
    console.log("[MachineStore.init] Waiting for authenticated state...");
    await when(() => AuthStore.auth.state === "authenticated");

    // Инициализируем autorun для фильтров
    autorun(async () => {
      const types = Array.from(this.filteredTypes);
      if (types.length === 0) {
        this.setExcludedMachineIds([]);
        return;
      }
      const machineClient = new MachineClient();
      const result = await machineClient.getMachinesByFilter(types, true);
      this.setExcludedMachineIds(result.data.machine_ids);
    });

    const machineClient = new MachineClient();
    const items = (await machineClient.getMachines()).data;
    this.setItems(items);

    this.initialized = true;
  }

  /**
   * Получает машину по идентификатору
   */
  async getMachineById(id: string): Promise<MachineItem | undefined> {
    const cached = this._machineCache.get(id);
    if (cached && Date.now() - cached.cachedAt < MACHINE_CACHE_TTL_MS) {
      return cached.data;
    }

    const machineClient = new MachineClient();
    const data = await machineClient.getMachineById(id);
    this._machineCache.set(id, { data: data.data, cachedAt: Date.now() });
    this.notifyListeners();

    return data.data;
  }
}

export const MachineStore = new MachineStoreFactory();

